import { useCallback, useEffect, useRef, useState } from "react";
import { Download, Sparkles, Trash2, AlertTriangle } from "lucide-react";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { Cost, PageHeader, Section, Spinner } from "./AiHtml";
import { useAuth } from "../context/AuthContext";
import { api, fileUrl, handleErr } from "../lib/api";

const Player = ({ m, testId }) =>
  m.type === "video" ? (
    <video data-testid={testId} src={fileUrl("media", m.mediaId)} controls className="w-full max-h-[70vh] rounded-lg bg-black" />
  ) : (
    <img data-testid={testId} src={fileUrl("media", m.mediaId)} alt={m.prompt} className="w-full max-h-[70vh] object-contain rounded-lg bg-black" />
  );

export const MediaStudio = ({ type, label, title, desc, controls, payload, cost, waitLabel }) => {
  const { refresh } = useAuth();
  const [prompt, setPrompt] = useState("");
  const [job, setJob] = useState(null);
  const [lib, setLib] = useState([]);
  const timer = useRef(null);

  const loadLib = useCallback(() => api.get("/media", { params: { type } }).then((r) => setLib(r.data.filter((m) => m.status === "done"))), [type]);
  useEffect(() => { loadLib(); return () => clearInterval(timer.current); }, [loadLib]);

  const poll = (id) => {
    clearInterval(timer.current);
    timer.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/media/${id}`);
        if (data.status !== "pending") {
          clearInterval(timer.current);
          setJob(data);
          refresh();
          if (data.status === "done") loadLib();
        }
      } catch { /* keep polling */ }
    }, 4000);
  };

  const start = async () => {
    try {
      const { data } = await api.post(type === "image" ? "/images" : "/videos", { prompt, ...payload });
      setJob(data);
      refresh();
      poll(data.mediaId);
    } catch (e) { handleErr(e); }
  };
  const remove = async (id) => { await api.delete(`/media/${id}`); loadLib(); };
  const pending = job?.status === "pending";

  return (
    <div className="space-y-6">
      <PageHeader label={label} title={title} desc={desc} />
      <div className="card-n p-5 space-y-5">
        <Textarea data-testid={`${type}-prompt-input`} rows={3} value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder="Describe what you want to create..." />
        {controls}
        <div className="flex items-center gap-3"><Cost n={cost} /><Button data-testid={`${type}-generate-btn`} disabled={pending || !prompt.trim()} onClick={start} className="rounded-full"><Sparkles size={16} className="mr-1.5" /> Generate {type}</Button></div>
      </div>
      {pending && <div className="card-n"><Spinner label={waitLabel} /></div>}
      {job?.status === "failed" && (
        <div data-testid={`${type}-error`} className="card-n p-5 border-red-500/40 flex items-start gap-3 text-red-300"><AlertTriangle size={18} className="shrink-0 mt-0.5" /><div><p className="font-medium">{job.error}</p><p className="text-xs text-slate-400 mt-1">Your credits have been refunded.</p></div></div>
      )}
      {job?.status === "done" && (
        <Section title="Preview" testId={`${type}-preview`} right={<a data-testid={`${type}-download-btn`} href={fileUrl("media", job.mediaId)} download className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-orange-500 hover:bg-orange-600 text-white"><Download size={13} /> Download</a>}>
          <Player m={job} testId={`${type}-preview-media`} />
          <p className="text-xs text-slate-500 mt-3">Saved to your library.</p>
        </Section>
      )}
      <Section title={`Your ${type} library (${lib.length})`} testId={`${type}-library`}>
        {lib.length === 0 ? <p className="text-sm text-slate-500">Nothing here yet.</p> : (
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
            {lib.map((m) => (
              <div key={m.mediaId} className="group relative rounded-lg overflow-hidden border border-slate-800 bg-black">
                {m.type === "video" ? <video src={fileUrl("media", m.mediaId)} className="w-full aspect-video object-cover" muted controls /> : <img src={fileUrl("media", m.mediaId)} alt={m.prompt} className="w-full aspect-square object-cover" loading="lazy" />}
                <div className="p-2 flex items-center gap-2 bg-[#0B1120]">
                  <p className="text-[11px] text-slate-400 truncate flex-1">{m.prompt}</p>
                  <a href={fileUrl("media", m.mediaId)} download className="text-slate-400 hover:text-orange-400"><Download size={13} /></a>
                  <button data-testid={`library-delete-${m.mediaId}`} onClick={() => remove(m.mediaId)} className="text-slate-400 hover:text-red-400"><Trash2 size={13} /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>
    </div>
  );
};
